// 04 — Daily coop. The home screen: the clock, the weather, the warning, the
// diorama, the flock, the two shortcuts and the chore bar in thumb reach.
//
// The Pixi world lives in the shell's stage layer; this screen owns only the box
// it is fitted into and the markers drawn over it. That is what keeps the world
// alive under a sheet and paused under an opaque screen.

import { onLocaleChange, t } from '../../i18n';
import { birdChip } from '../components/birdChip';
import { openChoreSheet } from './choreSheet';
import { feedScreen } from './feed';
import { choreBar, choreBarKey } from '../components/choreBar';
import { topBar } from '../components/topBar';
import { weatherPill } from '../components/weatherPill';
import { button, div, h, icon, paint } from '../dom';
import {
  selectBirdChips,
  selectChoreRows,
  selectEscapees,
  selectSpotMarkers,
  selectTopBar,
  selectWarningBanner,
  selectWeatherPill,
  selectYardSpotCount,
} from '../selectors';
import type { Router, Screen, ScreenContext } from '../router';
import { birdScreen } from './bird';
import { openCatchSheet } from './catchEscapee';
import { settingsButton } from './settings';
import { settingsScreen } from './settings';
import { broodyScreen } from './broody';
import { setupScreen } from './setup';
import { warningScreen } from './warning';

const WARNING_ICON = '<path d="M12 3 2 20h20z"></path><path d="M12 10v4"></path><path d="M12 17h.01"></path>';
// A fence, and the reason for the one banner on this screen that is not about a
// vital: the others warn that a bird is suffering, this one says she is gone.
const ESCAPEE_ICON = '<path d="M4 18h16"></path><path d="M8 18V9"></path><path d="M16 18V9"></path><path d="M12 9V4"></path>';

export function coopScreen(): Screen<HTMLElement> {
  // The view owns three listeners and the world's box; the router disposes it
  // when the screen leaves the stack, so they go with it.
  let view: CoopView | null = null;
  return {
    id: 'coop',
    showsStage: true,
    render: (ctx: ScreenContext<HTMLElement>) => {
      view?.dispose();
      view = createCoopView(ctx);
      return view.element;
    },
    dispose: () => {
      view?.dispose();
      view = null;
    },
  };
}

interface CoopView {
  element: HTMLElement;
  dispose: () => void;
}

function createCoopView(ctx: ScreenContext<HTMLElement>): CoopView {
  const { store, shell, game } = ctx.services;
  const topHost = div({ class: 'cf-stack cf-stack--tight' });
  const warningHost = div({});
  const escapeeHost = div({});
  const diorama = div({ class: 'cf-diorama' });
  const markers = div({ class: 'cf-diorama__markers' });
  diorama.appendChild(markers);
  const flockHost = div({ class: 'cf-flock cf-host' });
  const shortcutHost = div({ class: 'cf-row-grid cf-grid-2' });
  const choreHost = div({});

  const element = div(
    { class: 'cf-screen cf-screen--coop' },
    topHost,
    warningHost,
    escapeeHost,
    diorama,
    flockHost,
    shortcutHost,
    div({ class: 'cf-grow' }),
    choreHost,
  );
  element.tabIndex = -1;

  // --- the box the world is fitted into ------------------------------------
  // A ResizeObserver, not a window resize listener: the screen is built while
  // detached, so its box is zero until the router has mounted it, and a
  // rotation changes the box without firing anything useful. The observer fires
  // on attach and on every later size change.
  const observer = new ResizeObserver((entries) => {
    const box = entries[0]?.contentRect;
    if (!box || box.width <= 0 || box.height <= 0) return;
    // The stage is the column's background layer, so the canvas is placed at
    // the diorama's offset inside the column.
    const column = shell.column.getBoundingClientRect();
    const rect = diorama.getBoundingClientRect();
    void game
      .mountWorld()
      .then(() => game.world.setBox(rect.left - column.left, rect.top - column.top, box.width, box.height));
  });
  observer.observe(diorama);
  // The sim publishes a state change every tick, and this screen draws from
  // that state. Repainting at most once per frame is enough for a clock, and it
  // keeps a burst of ticks from turning into a burst of DOM writes.
  let paintQueued = false;
  const requestRender = (): void => {
    if (paintQueued) return;
    paintQueued = true;
    requestAnimationFrame(() => {
      paintQueued = false;
      render();
    });
  };
  const offLocale = onLocaleChange(requestRender);
  const offStore = store.subscribe(requestRender);

  function render(): void {
    const state = store.getState();

    const top = selectTopBar(state);
    const weather = selectWeatherPill(state);
    paint(
      topHost,
        `${top.week}|${top.day}|${top.clock}|${top.eggits}|${weather.temp}|${weather.location}|${weather.alertKey}|${weather.outlook?.map((c) => `${c.leagueDay}:${c.high}`).join(',') ?? ''}`,
      () =>
        div(
          { class: 'cf-stack cf-stack--tight' },
          div(
            { class: 'cf-topbar' },
            topBar(top),
            settingsButton(() => ctx.router.push(settingsScreen())),
          ),
          weatherPill(weather),
        ),
    );

    const warning = selectWarningBanner(state);
    paint(
      warningHost,
      warning ? `${warning.birdId}|${warning.causeKey}|${warning.ladderLabel}` : 'none',
      () =>
        warning
          ? button(
              {
                class: 'cf-banner',
                on: { click: () => openWarning(ctx.router, warning.birdId) },
              },
              icon({ shapes: WARNING_ICON, size: 22, tone: 'warn', strokeWidth: 2.2 }),
              h('span', {
                class: 'cf-banner__text',
                text: t(warning.causeKey, warning.causeParams),
              }),
              h('span', { class: 'cf-banner__count', text: warning.ladderLabel }),
            )
          : h('span'),
    );

    // The escapee banner sits under the vitals banner: an escaped bird is the
    // one thing on this screen the player has to go and do something about.
    const escapees = selectEscapees(state);
    paint(
      escapeeHost,
      escapees.map((bird) => `${bird.id}:${bird.oddsKey}`).join('|'),
      () =>
        escapees[0]
          ? button(
              {
                class: 'cf-banner',
                on: { click: () => openCatchSheet(ctx, escapees[0].id) },
              },
              icon({ shapes: ESCAPEE_ICON, size: 22, tone: 'danger', strokeWidth: 2.2 }),
              h('span', {
                class: 'cf-banner__text',
                text: t('escapee.banner', { name: escapees[0].name }),
              }),
              h('span', { class: 'cf-banner__count', text: t('escapee.bannerAction') }),
            )
          : h('span'),
    );

    const chips = selectBirdChips(state);
    paint(
      flockHost,
      chips.map((bird) => `${bird.id}:${bird.statusKey}:${bird.tone}`).join('|'),
      () =>
        div(
          { class: 'cf-flock' },
          ...chips.map((view) => birdChip({ view, onOpen: (id) => openBird(ctx.router, id) })),
        ),
    );

    const spots = selectSpotMarkers(state);
    paint(
      markers,
      spots.map((spot) => spot.n).join('|'),
      () =>
        div(
          { class: 'cf-diorama__markers' },
          ...spots.map((spot) =>
            h('span', {
              class: 'cf-spotmark',
              text: String(spot.n),
              attrs: { 'aria-label': t(spot.nameKey) },
              style: { left: `${spot.x * 100}%`, top: `${spot.y * 100}%` },
            }),
          ),
        ),
    );

    const broody = chips.find((bird) => bird.statusKey === 'bird.status.broody');
    // Open against total, so the shortcut reads "5 of 8" on a three-bird yard
    // rather than promising the five the mockup happened to draw (REFOCUS §7:
    // the backyard unlocks to eight as the flock grows).
    const yard = selectYardSpotCount(state);
    paint(shortcutHost, `${spots.length}|${yard.open}|${yard.total}|${broody?.id ?? ''}`, () =>
      div(
        { class: 'cf-row-grid cf-grid-2' },
        button({
          class: 'cf-shortcut',
          text: t('coop.yardSpots', { count: yard.open, total: yard.total }),
          on: { click: () => ctx.router.push(setupScreen()) },
        }),
        button({
          class: broody ? 'cf-shortcut cf-shortcut--warn' : 'cf-shortcut',
          text: broody ? t('coop.broodyIs', { name: broody.name }) : t('coop.broodyNone'),
          on: { click: () => ctx.router.push(broodyScreen()) },
        }),
      ),
    );

    // Feed has its own screen; every other chore opens the sheet.
    const rows = selectChoreRows(state);
    paint(choreHost, choreBarKey(rows), () =>
      choreBar({
        rows,
        onChore: (row) => {
          if (row.id === 'feed') ctx.router.push(feedScreen());
          else openChoreSheet(ctx, row.id);
        },
      }),
    );
  }

  render();

  return {
    element,
    dispose: () => {
      observer.disconnect();
      offLocale();
      offStore();
    },
  };
}

function openBird(router: Router<HTMLElement>, chickenId: string): void {
  router.push(birdScreen(), { chicken: chickenId });
}

/** The banner is about a specific bird's vitals, so it opens her warning. */
function openWarning(router: Router<HTMLElement>, chickenId: string): void {
  router.push(warningScreen(), { chicken: chickenId });
}
